import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { SevBadge } from '@/components/SevBadge';
import { EmptyState } from '@/components/EmptyState';
import { Check, Plus, Trash2 } from 'lucide-react';
import { formatRelativeTime } from '@/lib/locale';
import { apiDelete, apiGet, apiGetPaginated, apiPatch, qs } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';
import { toast } from '@/lib/toast';
import { AlertRuleModal } from './AlertRuleModal';

type AlertRule = {
  id: string;
  name: string;
  rule_type:
    | 'no_reports_received'
    | 'new_source_ip'
    | 'fail_ratio'
    | 'policy_downgrade'
    | 'volume_anomaly'
    | 'dns_record_changed';
  enabled: boolean;
  notify_serso_team: boolean;
  notify_klant: boolean;
  config?: Record<string, unknown>;
};

type AlertEvent = {
  id: string;
  rule_name?: string;
  severity: 'critical' | 'warning' | 'info';
  summary: string;
  triggered_at: string;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  fingerprint?: string;
};

type Tab = 'rules' | 'events';

export function AlertsScreen({ slug }: { slug: string }) {
  const [tab, setTab] = useState<Tab>('events');

  return (
    <div className="max-w-[1400px] space-y-5 p-5">
      <div>
        <h1 className="text-[22px] font-bold tracking-tight text-slate-900">Alerts</h1>
        <p className="mt-0.5 text-[13px] text-slate-500">Regels en events voor deze tenant</p>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200">
        <TabBtn current={tab} value="events" onClick={setTab}>
          Events
        </TabBtn>
        <TabBtn current={tab} value="rules" onClick={setTab}>
          Regels
        </TabBtn>
      </div>

      {tab === 'events' ? <EventsTab slug={slug} /> : <RulesTab slug={slug} />}
    </div>
  );
}

function EventsTab({ slug }: { slug: string }) {
  const q = useQuery({
    queryKey: ['alerts', 'events', slug],
    queryFn: () =>
      apiGetPaginated<AlertEvent>(`/alert-events${qs({ tenant: slug, page: 1, per_page: 50 })}`, {
        tenant: slug,
      }),
  });
  const rows = q.data?.data ?? [];
  return (
    <QueryState isLoading={q.isLoading} isError={q.isError} onRetry={() => q.refetch()}>
      {rows.length === 0 ? (
        <Card>
          <EmptyState
            title="Nog geen alerts"
            body="Er zijn geen actieve waarschuwingen voor deze tenant. Alerts verschijnen automatisch bij spoofing-pogingen, beleid-dalingen of onbekende verzendbronnen."
          />
        </Card>
      ) : (
        <Card>
          <CardHeader title="Events" subtitle={`${rows.length} regels`} />
          <div className="divide-y divide-slate-100">
            {rows.map((e) => (
              <div key={e.id} className={`px-4 py-3 ${e.acknowledged_at ? 'opacity-70' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <SevBadge sev={e.severity} />
                    <div>
                      <div className="text-[13px] text-slate-900">{e.summary}</div>
                      <div className="mt-0.5 font-mono text-[11px] text-slate-500">
                        {e.rule_name ?? '—'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[11px] text-slate-500">
                      {formatRelativeTime(e.triggered_at)}
                    </div>
                    {e.acknowledged_at && (
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-green-700">
                        <Check size={10} />
                        ack {e.acknowledged_by ? `door ${e.acknowledged_by}` : ''}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </QueryState>
  );
}

function RulesTab({ slug }: { slug: string }) {
  const [showModal, setShowModal] = useState(false);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['alerts', 'rules', slug],
    queryFn: () =>
      apiGet<AlertRule[]>(`/alert-rules${qs({ tenant: slug })}`, {
        tenant: slug,
      }),
  });

  const toggle = useMutation({
    mutationFn: (rule: AlertRule) =>
      apiPatch(`/tenants/${slug}/alert-rules/${rule.id}`, { enabled: !rule.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts', 'rules', slug] }),
    onError: (err: Error) =>
      toast({ tone: 'error', message: `Bijwerken mislukt: ${err.message}` }),
  });

  const remove = useMutation({
    mutationFn: (rule: AlertRule) => apiDelete(`/tenants/${slug}/alert-rules/${rule.id}`),
    onSuccess: () => {
      toast({ tone: 'success', message: 'Regel verwijderd' });
      qc.invalidateQueries({ queryKey: ['alerts', 'rules', slug] });
    },
    onError: (err: Error) =>
      toast({ tone: 'error', message: `Verwijderen mislukt: ${err.message}` }),
  });

  const rules = q.data ?? [];
  return (
    <>
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-brand-700"
        >
          <Plus size={14} /> Nieuwe regel
        </button>
      </div>
      <QueryState isLoading={q.isLoading} isError={q.isError} onRetry={() => q.refetch()}>
        {rules.length === 0 ? (
          <Card>
            <EmptyState
              title="Nog geen alert-regels"
              body="Configureer alert-regels om automatisch geïnformeerd te worden bij beleid-dalingen, spoofing-pogingen of nieuwe verzendbronnen."
            />
          </Card>
        ) : (
          <Card>
            <CardHeader title="Regels" subtitle={`${rules.length} actief`} />
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <Th>Naam</Th>
                    <Th>Type</Th>
                    <Th>SerSo notify</Th>
                    <Th>Klant notify</Th>
                    <Th>Status</Th>
                    <Th>Acties</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rules.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5 font-medium text-slate-900">{r.name}</td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-slate-600">
                        {r.rule_type}
                      </td>
                      <td className="px-3 py-2.5">{r.notify_serso_team ? 'Ja' : 'Nee'}</td>
                      <td className="px-3 py-2.5">{r.notify_klant ? 'Ja' : 'Nee'}</td>
                      <td className="px-3 py-2.5">
                        <button
                          type="button"
                          onClick={() => toggle.mutate(r)}
                          className="inline-flex items-center gap-1 text-[11px] hover:underline"
                        >
                          {r.enabled ? (
                            <>
                              <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
                              <span className="text-green-700">actief</span>
                            </>
                          ) : (
                            <>
                              <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                              <span className="text-slate-500">gepauzeerd</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Regel "${r.name}" verwijderen?`)) remove.mutate(r);
                          }}
                          className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:underline"
                          aria-label="Regel verwijderen"
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </QueryState>
      {showModal && <AlertRuleModal slug={slug} onClose={() => setShowModal(false)} />}
    </>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
      {children}
    </th>
  );
}

function TabBtn({
  current,
  value,
  onClick,
  children,
}: {
  current: Tab;
  value: Tab;
  onClick: (t: Tab) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`-mb-px border-b-2 px-3 py-2 text-[12px] ${
        active
          ? 'border-brand-600 font-semibold text-slate-900'
          : 'border-transparent text-slate-500 hover:text-slate-900'
      }`}
    >
      {children}
    </button>
  );
}
